import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../../backlog/backlog.css';
import './fieldIntake.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { DEFAULT_DATE_RANGE } from '../../../components/listDefaults';
import { DateRangeSelect, TRACKING_RANGES } from '../../modules/DateRangeSelect';
import { useNotify } from '../../../components/notifications';
import { runLoading } from '../../../components/loading';
import { fmtLocalDateTime } from '../../../utils/datetime';
import { consumePendingRecord } from '../../../layouts/navigateTo';
import {
  fetchFieldIntakeList, fetchFieldIntakeLookups, fetchFieldIntakePermissions, openFieldIntakeReport,
} from './api';
import { categoryName, dueState, errorText, FIELD_INTAKE_FORM, fromDate, intakePill } from './fieldIntakeLogic';
import FieldIntakeDetailPage from './FieldIntakeDetailPage';
import type { FieldIntakeLookups, FieldIntakeRow, IntakeStatus, Permissions } from './types';

/** 'Issues' = Needs review + Blocked (the prototype's filter). */
type StatusFilter = 'All' | 'Issues' | IntakeStatus;
const isIssue = (s: IntakeStatus) => s === 'Needs review' || s === 'Blocked';

const DUE_RULE = 'The due date the Job gets, as Sample Registration counts it: the received date and time plus the turnaround, in working days (weekends and holidays skipped). Until the receipt is recorded it is counted from the collection time.';


/**
 * Sampling Management ▸ Field Intake &amp; Job Creation — LIST VIEW.
 *
 * Field-app services whose record has been submitted. The laboratory selects one
 * and opens it with Edit (Alt+E / F2, or a double-click) to review what the
 * technician recorded, record the receipt, validate and create the ONE LIMS Job
 * for the service. The approved prototype is docs/prototypes/field-intake-job-creation.html
 * in the Field app repo; ADR-0021.
 */
export default function FieldIntakePage() {
  const defaultDays = TRACKING_RANGES.find(r => r.label === DEFAULT_DATE_RANGE.FieldDataTracking)?.days ?? 90;
  const [rows, setRows]           = useState<FieldIntakeRow[]>([]);
  const [loading, setLoading]     = useState(true);
  const [loadMs, setLoadMs]       = useState<number | null>(null);
  const [error, setError]         = useState<string | null>(null);
  const [days, setDays]           = useState<number | null>(defaultDays);
  const [statusFilt, setStatusFilt] = useState<StatusFilter>('All');
  const [search, setSearch]       = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [current, setCurrent]     = useState<string | null>(null);
  const [openId, setOpenId]       = useState<string | null>(() => consumePendingRecord('fieldintake'));
  const [perm, setPerm]           = useState<Permissions>({ canRead: true, canWrite: false, isAdmin: false });
  const [lookups, setLookups]     = useState<FieldIntakeLookups | null>(null);
  const notify = useNotify();
  const searchRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const t0 = performance.now();
    try {
      setRows(await fetchFieldIntakeList(fromDate(days)));
      setSelectedIds(new Set());
    } catch (e) {
      setError(errorText(e, 'Failed to load'));
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [days]);

  const loadLookups = useCallback(async () => {
    try { setLookups(await fetchFieldIntakeLookups()); }
    catch (e) { notify.error(errorText(e, 'Could not load the laboratory staff list.')); }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    void loadLookups();
    void (async () => {
      try { setPerm(await fetchFieldIntakePermissions()); }
      catch { setPerm({ canRead: true, canWrite: false, isAdmin: false }); }   // read-only when the rights cannot be read
    })();
  }, [loadLookups]);

  const filtered = useMemo(() => {
    let r = statusFilt === 'All' ? rows
      : statusFilt === 'Issues' ? rows.filter(x => isIssue(x.intakeStatus))
      : rows.filter(x => x.intakeStatus === statusFilt);
    const t = search.trim().toLowerCase();
    if (t) {
      r = r.filter(x => [x.serviceNo, x.requestNo, x.jobID, x.clientName, x.projectCode, x.projectName, x.siteAddress, x.technicianName]
        .some(v => (v ?? '').toLowerCase().includes(t)));
    }
    // The prototype's order: services still without a Job first, then by due date (earliest
    // first; no due date last). A column header click still re-sorts in the grid.
    const dueMs = (x: FieldIntakeRow) => (x.dueDate ? new Date(x.dueDate).getTime() : Number.MAX_SAFE_INTEGER);
    return [...r].sort((a, b) => (Number(!!a.jobID) - Number(!!b.jobID)) || (dueMs(a) - dueMs(b)));
  }, [rows, statusFilt, search]);

  const count = (s: IntakeStatus) => rows.filter(r => r.intakeStatus === s).length;
  const currentRow = rows.find(r => r.serviceTaskId === current) ?? null;
  const visibleCurrent = currentRow && filtered.some(r => r.serviceTaskId === current) ? currentRow : null;

  const onSelection = (ids: Set<string | number>) => {
    const added = [...ids].find(id => !selectedIds.has(id));
    setSelectedIds(ids);
    if (added) setCurrent(String(added));
    else if (current && !ids.has(current)) setCurrent(ids.size ? String([...ids].pop()) : null);
  };

  const preview = async () => {
    if (!visibleCurrent) return;
    try {
      const shown = await runLoading(openFieldIntakeReport(visibleCurrent.serviceTaskId), { blocking: true, label: 'Composing the report…' });
      if (!shown) notify.warning('The browser blocked the report window. Allow pop-ups for this site, then try again.');
    } catch (e) {
      notify.error(errorText(e, 'Could not open the report.'));
    }
  };


  const cols: AppColumn<FieldIntakeRow>[] = [
    { field: 'serviceNo', header: 'Service ID', width: 130, text: r => r.serviceNo ?? '',
      render: r => <button type="button" className="adg-celllink bl-link" onClick={e => { e.stopPropagation(); setOpenId(r.serviceTaskId); }}>{r.serviceNo}</button> },
    { field: 'requestNo', header: 'Request ID', width: 110 },
    { field: 'clientName', header: 'Client', width: 180 },
    { field: 'project', header: 'Project', width: 190, text: r => [r.projectCode, r.projectName !== r.projectCode ? r.projectName : null].filter(Boolean).join(' · ') },
    { field: 'siteAddress', header: 'Site address', width: 230 },
    { field: 'serviceType', header: 'Service type', width: 200, text: r => [categoryName(r.categoryCode), r.serviceType].filter(Boolean).join(' · ') },
    { field: 'technicianName', header: 'Technician', width: 130 },
    { field: 'collectedText', header: 'Collected', width: 100 },
    { field: 'due', header: <span title={DUE_RULE}>Due ⓘ</span>, headerText: 'Due', width: 170,
      // Grid standard: plain text — colour belongs to the status column only. Overdue is said in words.
      text: r => {
        const d = dueState(r.dueDate);
        return d ? `${d.overdue && !r.jobID ? 'Overdue · ' : ''}${fmtLocalDateTime(r.dueDate)}` : '';
      },
      render: r => {
        const d = dueState(r.dueDate);
        if (!d) return null;
        const title = r.dueEstimated ? 'Estimated: counted from the collection time until the receipt is recorded.' : undefined;
        return <span title={title}>{d.overdue && !r.jobID ? 'Overdue · ' : ''}{fmtLocalDateTime(r.dueDate)}</span>;
      } },
    { field: 'sampleCount', header: 'Samples', width: 80, type: 'number', align: 'right' },
    { field: 'photoCount', header: 'Photos', width: 80, type: 'number', align: 'right' },
    { field: 'stage', header: 'Field record', width: 140, text: r => r.stage },
    { field: 'intakeStatus', header: 'Intake status', width: 130, text: r => r.intakeStatus,
      render: r => <span className={`bl-pill ${intakePill(r.intakeStatus)}`}>{r.intakeStatus}</span> },
    { field: 'jobID', header: 'Job ID', width: 110 },
  ];

  // ── List → detail ──────────────────────────────────────────────────────────
  if (openId) {
    return (
      <FieldIntakeDetailPage
        serviceTaskId={openId}
        ids={filtered.map(r => r.serviceTaskId)}
        perm={perm}
        lookups={lookups}
        onNavigate={id => { setOpenId(id); setCurrent(id); }}
        onBack={() => { setCurrent(openId); setOpenId(null); void load(); }}
      />
    );
  }

  return (
    <div className="bl-page fi-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span>
            <i className="ti ti-chevron-right" />
            <span>Sampling Management</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Field Intake &amp; Job Creation</span>
          </div>
          <div className="dp-title-row">
            <FormTitle auditFormName={FIELD_INTAKE_FORM} loadMs={loadMs} loading={loading} />
          </div>
          <p className="fi-lead">
            Services the ALPACA Field app has sent to LIMS. Select a row and click <b>Edit</b> (or double-click it) to open it.
            Review the technician&rsquo;s survey, samples, photos and signatures, record the laboratory receipt, validate,
            then create the Laboratory Job ID.
          </p>
        </div>

        <div className="kpi-row">
          <div className="kpi">
            <div className="kpi-lbl">All field records</div>
            <div className="kpi-val kpi-blue">{loading ? '…' : rows.length}</div>
            <div className="kpi-chg">Submitted from the field app</div>
          </div>
          <div className="kpi">
            <div className="kpi-lbl">Ready for Job ID</div>
            <div className="kpi-val kpi-teal">{loading ? '…' : count('Ready')}</div>
            <div className="kpi-chg">All checks pass</div>
          </div>
          <div className="kpi">
            <div className="kpi-lbl">Needs review / blocked</div>
            <div className="kpi-val kpi-red">{loading ? '…' : count('Needs review') + count('Blocked')}</div>
            <div className="kpi-chg">Receipt or checks outstanding</div>
          </div>
          <div className="kpi">
            <div className="kpi-lbl">Job IDs created</div>
            <div className="kpi-val" style={{ color: 'var(--ai-blue)' }}>{loading ? '…' : count('Job created')}</div>
            <div className="kpi-chg">In this range</div>
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            left={
              <>
                <DateRangeSelect variant="dropdown" label="Date Range" ranges={TRACKING_RANGES} value={days} onChange={setDays} />
              </>
            }
            onEdit={() => visibleCurrent && setOpenId(visibleCurrent.serviceTaskId)}
            editDisabled={!visibleCurrent}
            onPreview={() => void preview()}
            previewDisabled={!visibleCurrent?.ecocNo}
            onRefresh={() => void load()}
            refreshing={loading}
            exportData={{ filename: 'field-intake', ...gridExportMatrix(filtered, cols) }}
          />
        </div>

        <div className="list-search-row">
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input ref={searchRef} type="text" value={search} onChange={e => setSearch(e.target.value)}
                   placeholder="Search Service ID, Job ID, client, project, address…" />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
          </div>
          {filtered.length !== rows.length && (
            <span className="search-match-count">{filtered.length} match{filtered.length !== 1 ? 'es' : ''}</span>
          )}
        </div>

        <div className="bl-card bl-card-flex">
          <div className="sa-filter-row">
            <span className="sa-filter-lbl">Intake status:</span>
            {(['All', 'Issues', 'Ready', 'Needs review', 'Blocked', 'Job created'] as StatusFilter[]).map(s => (
              <button key={s} className={`sa-filter-btn${statusFilt === s ? ' active' : ''}`} onClick={() => setStatusFilt(s)}
                      title={s === 'Issues' ? 'Needs review and Blocked' : undefined}>
                {s} ({s === 'All' ? rows.length : s === 'Issues' ? rows.filter(x => isIssue(x.intakeStatus)).length : count(s)})
              </button>
            ))}
          </div>
          {error ? (
            <div className="bl-err" style={{ margin: 12 }}>
              <i className="ti ti-alert-circle" /> {error}
              <button className="fi-link" style={{ marginLeft: 10 }} onClick={() => void load()}>Retry</button>
            </div>
          ) : (
            <AppDataGrid<FieldIntakeRow>
              rows={filtered}
              columns={cols}
              getRowId={r => r.serviceTaskId}
              selectable
              selectedIds={selectedIds}
              onSelectionChange={onSelection}
              onRowClick={r => setCurrent(r.serviceTaskId)}
              onRowDoubleClick={r => setOpenId(r.serviceTaskId)}
              getRowClassName={r => (r.serviceTaskId === current ? 'adg-selected' : undefined)}
              maximizable
              exportFilename="field-intake"
              loading={loading}
              loadMs={loadMs}
              persistKey="fieldintake-list"
              emptyText="No field records in this range."
            />
          )}
        </div>
      </div>

    </div>
  );
}
